import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createAccountCommand,
  deleteAccountCommand,
  listAccountCommands,
  updateAccountCommand,
} from "../../api/endpoints/accountCommands.js";

export function useAccountCommands(accountId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!accountId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await listAccountCommands(accountId);
      setItems(rows);
    } catch (err) {
      setError(err.message ?? "Failed to load commands");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const commandsForAsset = useCallback(
    (assetId) => items.filter((row) => row.assetId === assetId),
    [items],
  );

  const commandCountByAsset = useMemo(() => {
    const counts = {};
    for (const row of items) {
      counts[row.assetId] = (counts[row.assetId] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  const addCommand = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createAccountCommand(accountId, payload);
      setItems((list) => [created, ...list]);
      return created;
    } catch (err) {
      setError(err.message ?? "Failed to create command");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const saveCommand = useCallback(async (id, payload) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateAccountCommand(accountId, id, payload);
      setItems((list) => list.map((row) => (row.id === id ? updated : row)));
      return updated;
    } catch (err) {
      setError(err.message ?? "Failed to save command");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  const removeCommand = useCallback(async (id) => {
    setSaving(true);
    setError(null);
    try {
      await deleteAccountCommand(accountId, id);
      setItems((list) => list.filter((row) => row.id !== id));
    } catch (err) {
      setError(err.message ?? "Failed to delete command");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [accountId]);

  return {
    allItems: items,
    loading,
    saving,
    error,
    commandsForAsset,
    commandCountByAsset,
    addCommand,
    saveCommand,
    removeCommand,
    reload,
  };
}
